import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './Leaderboard.css';

const periodLabels = {
  weekly: 'This week',
  monthly: 'This month',
  all: 'All time',
};

const Leaderboard = () => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(0);
  const [period, setPeriod] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, [period]);

  const fetchLeaderboard = async () => {
    try {
      const response = await api.get(
        `/gamification/leaderboard?period=${period}&limit=50`
      );
      setLeaderboard(response.data.leaderboard);
      setUserRank(response.data.userRank);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return { icon: '🥇', class: 'gold' };
    if (rank === 2) return { icon: '🥈', class: 'silver' };
    if (rank === 3) return { icon: '🥉', class: 'bronze' };
    return { icon: rank, class: 'normal' };
  };

  const getMotivationText = () => {
    if (userRank <= 10) return 'Elite learner status unlocked!';
    if (userRank <= 50) return 'Great momentum—keep climbing.';
    if (userRank === 0) return 'Complete a course to join the ranks.';
    return 'Every lesson completed moves you up.';
  };

  if (loading) {
    return <div className="loading">Loading leaderboard...</div>;
  }

  return (
    <div className="page leaderboard-page">
      <div className="container">
        <section className="leaderboard-hero">
          <div className="hero-text">
            <p className="eyebrow">Community</p>
            <h1>Leaderboard</h1>
            <p>
              See who is leading the learning sprint. Rankings update based on
              points earned for completing lessons, courses, and challenges.
            </p>
            <div className="period-pills">
              {Object.entries(periodLabels).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`pill ${period === key ? 'active' : ''}`}
                  onClick={() => setPeriod(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="hero-rank-card">
            <span className="label">Your rank</span>
            <p className="rank-value">#{userRank || '—'}</p>
            <p className="rank-note">{getMotivationText()}</p>
          </div>
        </section>

        {leaderboard.length >= 3 && (
          <section className="podium">
            {[
              { index: 1, className: 'second', medal: '🥈' },
              { index: 0, className: 'first', medal: '🥇' },
              { index: 2, className: 'third', medal: '🥉' },
            ].map(({ index, className, medal }) => {
              const entry = leaderboard[index];
              if (!entry) return null;

              return (
                <div key={entry.id} className={`podium-item ${className}`}>
                  <div className="avatar">
                    {entry.avatar_url ? (
                      <img src={entry.avatar_url} alt="" />
                    ) : (
                      <span>
                        {entry.first_name?.charAt(0)}
                        {entry.last_name?.charAt(0)}
                      </span>
                    )}
                  </div>
                  <span className="medal">{medal}</span>
                  <h4>
                    {entry.first_name} {entry.last_name?.charAt(0)}.
                  </h4>
                  <p className="points">{entry.points.toLocaleString()} pts</p>
                </div>
              );
            })}
          </section>
        )}

        <section className="leaderboard-table">
          <div className="table-header">
            <span>Rank</span>
            <span>Member</span>
            <span>Points</span>
            {period === 'all' && <span>Highlights</span>}
          </div>

          {leaderboard.map((entry) => {
            const badge = getRankBadge(parseInt(entry.rank, 10));
            const isCurrentUser = entry.id === user.id;
            return (
              <div
                key={entry.id}
                className={`table-row ${isCurrentUser ? 'current-user' : ''}`}
              >
                <span className={`rank ${badge.class}`}>{badge.icon}</span>
                <div className="member">
                  <div className="user-avatar">
                    {entry.avatar_url ? (
                      <img src={entry.avatar_url} alt="" />
                    ) : (
                      <span>
                        {entry.first_name?.charAt(0)}
                        {entry.last_name?.charAt(0)}
                      </span>
                    )}
                  </div>
                  <div>
                    <strong>
                      {entry.first_name} {entry.last_name}
                    </strong>
                    {isCurrentUser && <span className="you-badge">You</span>}
                  </div>
                </div>
                <span className="points-value">
                  {entry.points.toLocaleString()}
                </span>
                {period === 'all' && (
                  <span className="highlight-text">
                    {entry.courses_completed || 0} courses ·{' '}
                    {entry.certificates || 0} certs
                  </span>
                )}
              </div>
            );
          })}
        </section>
      </div>
    </div>
  );
};

export default Leaderboard;
